import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Home,
  Smartphone,
  Brain,
  User,
  ChevronRight,
  X,
  Sun,
  Moon,
  Crown,
  LogOut,
  GraduationCap,
  SquarePlay,
} from 'lucide-react';
import logo from '../assets/logo.png';

interface SidebarProps {
  activeView: string;
  onViewChange: (view: string) => void;
  isMobileDrawer?: boolean;
  onCloseMobileDrawer?: () => void;
  isDarkMode?: boolean;
  onDarkModeToggle?: () => void;
  subscriptionTier?: 'free' | 'pro' | 'max';
  onLogout?: () => void;
}

const menuItems = [
  { id: 'হোম', label: 'হোম', icon: Home },
  { id: 'মোবাইল হোম', label: 'মোবাইল হোম', icon: Smartphone },
  { id: 'কোর্সসমূহ', label: 'কোর্সসমূহ', icon: SquarePlay },
  { id: 'চর্চাঘর', label: 'অনুশীলন', icon: Brain },
  { id: 'প্রোফাইল', label: 'প্রোফাইল', icon: User },
];

export default function Sidebar({ activeView, onViewChange, isMobileDrawer, onCloseMobileDrawer, isDarkMode, onDarkModeToggle, subscriptionTier, onLogout }: SidebarProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isInstructor, setIsInstructor] = useState(false);
  const [profileTrigger, setProfileTrigger] = useState(0);

  // Sync profile details dynamically when updated
  useEffect(() => {
    const handleProfileUpdate = () => {
      setProfileTrigger(prev => prev + 1);
    };
    window.addEventListener('sh_profile_updated', handleProfileUpdate);
    return () => {
      window.removeEventListener('sh_profile_updated', handleProfileUpdate);
    };
  }, []);

  // Check if user is an approved instructor
  useEffect(() => {
    const shAuth = (window as any).shAuth;
    if (shAuth && shAuth.user) {
      setIsInstructor(!!shAuth.user.is_instructor);
    } else {
      // In dev mode (localhost), allow showing instructor dashboard for testing
      const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      setIsInstructor(isLocal);
    }
  }, [profileTrigger]);

  const visibleMenuItems = [
    ...menuItems.filter(item => {
      if (item.id === 'মোবাইল হোম') {
        return !!isMobileDrawer;
      }
      return true;
    }),
    ...(isInstructor ? [{ id: 'শিক্ষক ড্যাশবোর্ড', label: 'শিক্ষক ড্যাশবোর্ড', icon: GraduationCap }] : []),
  ];

  return (
    <aside className={
      isMobileDrawer
        ? "w-[280px] h-full bg-white dark:bg-brand-dark-bg flex flex-col z-50 border-r border-gray-100 dark:border-slate-800 transition-colors duration-200"
        : "hidden lg:flex w-[280px] h-screen bg-white dark:bg-brand-dark-bg fixed left-0 top-0 flex flex-col border-r border-gray-100 dark:border-slate-800 z-50 transition-colors duration-200"
    }>
      {/* Logo */}
      <div className="px-6 py-6 flex items-center justify-between gap-2">
        <button
          onClick={() => onViewChange('চর্চাঘর')}
          className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity focus:outline-none cursor-pointer min-w-0"
        >
          <img src={logo} alt="শুদ্ধশিক্ষা" className="w-[50px] h-[50px] rounded-xl object-cover flex-shrink-0" />
          <div className="flex flex-col justify-center items-start min-w-0">
            <h1 className="font-bangla text-[17px] leading-tight font-semibold text-[#1F1F1F] dark:text-[#E8EAED] truncate">শুদ্ধশিক্ষা</h1>
            <p className="font-bangla text-[10px] leading-none text-gray-400 dark:text-[#A0A5AD] font-medium tracking-wide mt-0.5 truncate">শেখা হোক সহজ</p>
          </div>
        </button>
        
        {/* Toggle Switch */}
        {!isMobileDrawer && onDarkModeToggle && (
          <button
            onClick={onDarkModeToggle}
            className="relative flex items-center w-[72px] h-9 bg-gray-100 dark:bg-[#202020] border border-transparent dark:border-white/5 rounded-full p-1 cursor-pointer select-none focus:outline-none transition-colors duration-300 flex-shrink-0"
            aria-label="Toggle theme"
          >
            <motion.div
              className="absolute w-7 h-7 bg-white dark:bg-[#3A3A3C] rounded-full shadow-sm"
              animate={{ x: isDarkMode ? 36 : 0 }}
              transition={{ type: "spring", stiffness: 350, damping: 25 }}
            />
            <div className="relative z-10 w-7 h-7 flex items-center justify-center">
              <Sun className={`w-4.5 h-4.5 transition-colors duration-200 ${isDarkMode ? 'text-gray-500' : 'text-amber-500'}`} />
            </div>
            <div className="flex-1" />
            <div className="relative z-10 w-7 h-7 flex items-center justify-center">
              <Moon className={`w-4.5 h-4.5 transition-colors duration-200 ${isDarkMode ? 'text-white' : 'text-gray-400'}`} />
            </div>
          </button>
        )}

        {isMobileDrawer && onCloseMobileDrawer && (
          <button
            onClick={onCloseMobileDrawer}
            className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-white/5 flex items-center justify-center text-gray-500 dark:text-[#A0A5AD] hover:bg-gray-100 dark:hover:bg-white/10 transition-colors cursor-pointer flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-1">
        {visibleMenuItems.map((item) => {
          const isActive = activeView === item.id;
          const isHovered = hoveredItem === item.id;
          const Icon = item.icon;

          return (
            <button
              key={item.id}
              onClick={() => onViewChange(item.id)}
              onMouseEnter={() => setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
              className="relative w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-colors duration-200 group cursor-pointer"
            >
              {isActive && (
                <motion.div
                  layoutId="activeSidebarBg"
                  className="absolute inset-0 bg-[#1F1F1F] dark:bg-[#8AB4F8] dark:shadow-[0_0_15px_rgba(138,180,248,0.25)] rounded-2xl shadow-lg"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
              {!isActive && isHovered && (
                <div className="absolute inset-0 bg-[#F0F1F5] dark:bg-white/5 rounded-2xl" />
              )}

              <span className="relative z-10">
                <Icon
                  className={`w-5 h-5 transition-colors duration-200 ${
                    isActive ? 'text-white dark:text-[#121212]' : 'text-[#64748b] dark:text-[#A0A5AD] group-hover:text-[#1F1F1F] dark:group-hover:text-[#8AB4F8]'
                  }`}
                />
              </span>
              <span
                className={`relative z-10 font-bangla text-[15px] font-medium flex-1 text-left transition-colors duration-200 ${
                  isActive ? 'text-white dark:text-[#121212]' : 'text-[#64748b] dark:text-[#A0A5AD] group-hover:text-[#1F1F1F] dark:group-hover:text-[#8AB4F8]'
                }`}
              >
                {item.label}
              </span>
              {isActive && (
                <ChevronRight className={`relative z-10 w-4 h-4 transition-colors duration-200 ${isActive ? 'text-white dark:text-[#121212]' : ''}`} />
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="px-4 py-4 border-t border-gray-100 dark:border-white/5">
        <div className="w-full flex items-center justify-between gap-1">
          <button
            key={profileTrigger}
            onClick={() => onViewChange('প্রোফাইল')}
            className="flex-1 flex items-center gap-3 hover:bg-[#F0F1F5]/40 dark:hover:bg-white/5 p-2 rounded-2xl transition-all duration-200 cursor-pointer text-left focus:outline-none min-w-0"
          >
            {(() => {
              const shAuth = (window as any).shAuth;
              const userName = shAuth?.user?.name || 'রাহুল আহমেদ';
              const rawClass = String(shAuth?.user?.class || 'ssc_2027');
              const classTranslation: Record<string, string> = {
                'class-6': 'ষষ্ঠ শ্রেণী',
                'class-7': 'সপ্তম শ্রেণী',
                'class-8': 'অষ্টম শ্রেণী',
                'class-9': 'নবম শ্রেণী',
                'class-10': 'দশম শ্রেণী',
                'hsc-1': 'এইচএসসি ১ম বর্ষ',
                'hsc-2': 'এইচএসসি ২য় বর্ষ',
                'ssc-2027': 'এসএসসি ২০২৭',
                'ssc_2027': 'এসএসসি ২০২৭',
                'hsc-2026': 'এইচএসসি ২০২৬',
                'hsc_2026': 'এইচএসসি ২০২৬',
              };
              const userClass = classTranslation[rawClass] || classTranslation[rawClass.toLowerCase()] || rawClass;
              const avatarInit = userName.charAt(0) || 'রা';
              return (
                <>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00CBA9] to-[#00A3FF] dark:from-[#8AB4F8]/20 dark:to-[#8AB4F8]/5 dark:border dark:border-[#8AB4F8]/20 dark:text-[#8AB4F8] flex items-center justify-center text-white font-bangla font-bold text-sm flex-shrink-0 overflow-hidden">
                    {shAuth?.user?.avatar ? (
                      <img
                        src={shAuth.user.avatar}
                        alt={userName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      avatarInit
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="font-bangla text-sm font-semibold text-[#1F1F1F] dark:text-[#E8EAED] truncate">{userName}</p>
                      {subscriptionTier && subscriptionTier !== 'free' && (
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-black tracking-wider flex-shrink-0 uppercase ${
                          subscriptionTier === 'max'
                            ? 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-400 border border-violet-500/25'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border border-amber-500/25'
                        }`}>
                          {subscriptionTier}
                        </span>
                      )}
                    </div>
                    <p className="font-bangla text-xs text-gray-400 dark:text-[#A0A5AD]">{userClass}</p>
                  </div>
                </>
              );
            })()}
          </button>

          {onLogout && (
            <button
              onClick={onLogout}
              className="p-2.5 rounded-xl hover:bg-red-50 dark:hover:bg-red-500/10 text-gray-400 hover:text-red-500 dark:text-[#A0A5AD] dark:hover:text-red-400 transition-colors cursor-pointer"
              title="লগআউট"
            >
              <LogOut className="w-4.5 h-4.5" />
            </button>
          )}
        </div>
        {subscriptionTier === 'free' && (
          <button
            onClick={() => onViewChange('সাবস্ক্রিপশন')}
            className="mt-3.5 w-full py-2 px-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bangla text-xs font-bold shadow-md hover:shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <Crown className="w-3.5 h-3.5" />
            প্রো-তে আপগ্রেড করুন
          </button>
        )}
      </div>
    </aside>
  );
}
